import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import { useLanguage } from "../../contexts/LanguageContext";
import { Copy, CheckCircle2, ExternalLink, Eye, Users, TrendingUp, Code2 } from "lucide-react";

/**
 * V178 — Partner Embeddable Widget tab.
 *
 * Lets logged-in users grab a one-line <script> snippet they can paste on
 * their own site to display the TopRankPulse "AI Autonomy Live" ticker.
 * Visits to the iframe and clicks on the CTA are attributed to the user's
 * partner_id so we can track affiliate performance and pay revenue share.
 */
export default function PartnerWidgetTab({ user }) {
  const { t } = useLanguage();
  const partnerId = (user?.referral_code
    || (user?.user_id ? `u_${String(user.user_id).slice(-8)}` : "")
    || (user?.email ? `e_${String(user.email).split("@")[0].toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 12)}` : "guest"));

  const origin = (typeof window !== "undefined") ? window.location.origin : "";
  const snippet = `<script async src="${origin}/api/agent-autonomy/embed/widget.js" data-partner-id="${partnerId}"></script>`;

  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState(null);
  const [previewReady, setPreviewReady] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/agent-autonomy/embed/stats`, {
        params: { partner_id: partnerId },
        withCredentials: true,
      });
      const me = (res.data?.partners || []).find(p => p.partner_id === partnerId);
      setStats(me || { partner_id: partnerId, total_hits: 0, hits_24h: 0, unique_visitor_days: 0 });
    } catch {
      setStats({ partner_id: partnerId, total_hits: 0, hits_24h: 0, unique_visitor_days: 0 });
    }
  }, [partnerId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {}
  };

  const iframeSrc = `${origin}/api/agent-autonomy/embed/iframe.html?partner_id=${encodeURIComponent(partnerId)}`;
  const estPayout = ((stats?.unique_visitor_days || 0) * 0.10).toFixed(2);

  return (
    <div className="space-y-6" data-testid="partner-widget-tab">
      {/* Header */}
      <div className="bg-gradient-to-br from-violet-950 via-purple-900 to-fuchsia-900 text-white p-6 lg:p-8 rounded-2xl shadow-xl border border-white/10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-emerald-300 font-extrabold mb-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> {t.pw_program_live}
            </div>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight">{t.pw_title}</h1>
            <p className="text-violet-200 mt-2 text-sm max-w-2xl">
              {t.pw_lead}
              {" "}<strong className="text-emerald-300">{t.pw_revshare}: €0,10 {t.pw_per_unique}</strong>.
            </p>
          </div>
          <div className="bg-black/30 rounded-xl px-4 py-3 border border-white/10 min-w-[180px]">
            <div className="text-[9px] tracking-[0.2em] text-violet-300 uppercase font-bold">{t.pw_your_id}</div>
            <div className="text-lg font-mono font-bold text-white mt-1" data-testid="partner-id-value">{partnerId}</div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Eye} label={t.pw_stat_total} value={stats?.total_hits ?? "—"} testid="stat-total" color="text-violet-600" />
        <StatCard icon={TrendingUp} label={t.pw_stat_24h} value={stats?.hits_24h ?? "—"} testid="stat-24h" color="text-emerald-600" />
        <StatCard icon={Users} label={t.pw_stat_unique} value={stats?.unique_visitor_days ?? "—"} testid="stat-unique" color="text-fuchsia-600" />
        <StatCard icon={TrendingUp} label={t.pw_stat_payout} value={`€${estPayout}`} testid="stat-payout" color="text-amber-600" />
      </div>

      {/* Snippet */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 lg:p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Code2 className="w-5 h-5 text-violet-600" />
          <h2 className="text-base font-bold text-gray-900">{t.pw_snippet_h}</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          {t.pw_snippet_lead}
        </p>
        <div className="relative">
          <pre className="bg-gray-900 text-emerald-300 text-xs lg:text-sm p-4 rounded-lg overflow-x-auto font-mono leading-relaxed pr-24" data-testid="embed-snippet">
            {snippet}
          </pre>
          <button
            type="button"
            onClick={handleCopy}
            className={`absolute right-3 top-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
              copied ? "bg-emerald-500 text-white" : "bg-white text-gray-900 hover:bg-gray-100"
            }`}
            data-testid="copy-snippet-button"
          >
            {copied ? <><CheckCircle2 className="w-3.5 h-3.5" /> {t.pw_copied}</> : <><Copy className="w-3.5 h-3.5" /> {t.pw_copy}</>}
          </button>
        </div>
        <div className="flex items-center gap-3 mt-4 text-xs text-gray-500">
          <a
            href={iframeSrc}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-violet-600 hover:text-violet-700 font-semibold"
            data-testid="open-iframe-link"
          >
            {t.pw_open_window} <ExternalLink className="w-3 h-3" />
          </a>
          <span>·</span>
          <button
            type="button"
            onClick={fetchStats}
            className="text-gray-500 hover:text-gray-900 font-semibold"
            data-testid="refresh-stats-button"
          >
            {t.pw_refresh}
          </button>
        </div>
      </div>

      {/* Live preview */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 lg:p-6 shadow-sm">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-base font-bold text-gray-900">{t.pw_preview_h}</h2>
          <span className="text-[10px] uppercase tracking-widest text-emerald-600 font-extrabold">
            {previewReady ? t.pw_loaded : t.pw_loading}
          </span>
        </div>
        <div className="rounded-xl overflow-hidden bg-black/5 border border-gray-200" data-testid="widget-preview-container">
          <iframe
            src={iframeSrc}
            title="TopRankPulse AI Autonomy Live Preview"
            style={{ width: "100%", height: "380px", border: 0, display: "block" }}
            loading="lazy"
            onLoad={() => setPreviewReady(true)}
            data-testid="widget-preview-iframe"
          />
        </div>
      </div>

      {/* Tips */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5">
        <h3 className="text-sm font-bold text-amber-900 mb-2">{t.pw_tips_h}</h3>
        <ul className="space-y-1.5 text-sm text-amber-900/90">
          <li>· {t.pw_tip_1}</li>
          <li>· {t.pw_tip_2}</li>
          <li>· {t.pw_tip_3}</li>
        </ul>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-gray-500 font-bold">
        <Icon className={`w-3.5 h-3.5 ${color}`} /> {label}
      </div>
      <div className="text-2xl font-black text-gray-900 mt-1.5">{value}</div>
    </div>
  );
}
